import { ANALYTICS_READY_EVENT } from '#existing-tag-analytics';
/**
 * Cart mounting panel (2026-09-13, docs/mounting/contract.md): the binding
 * mounting question inside the cart drawer for the two paths the product
 * page cannot answer alone:
 *   - a flat ski bought with a separate binding (Issue #201): the binding
 *     line gets the question and the ski pairing;
 *   - a ski that ships with a binding next to a ski boot in the cart: the
 *     option "adjust to that boot" (theme setting mounting_cart_boot_enabled).
 * The "sole length follows" option rides along on both (theme setting
 * mounting_bsl_pending_enabled).
 *
 * Liquid renders the facts island (snippets/cart-drawer-mounting-facts
 * .liquid, inside <cart-mounting-root> in #CartDrawer-CartItems) and one
 * hidden <fieldset data-cart-mounting> per eligible line (snippets/cart-
 * drawer-mounting.liquid). This element wakes up on every list swap, plans
 * with assets/cart-mounting-state.js, shows the panels, applies at most ONE
 * reconcile write per render, and turns the customer's choice into a
 * /cart/change POST that rewrites the line's properties.
 *
 * DOM rules that cost time before (keep them):
 *   - every `change` inside a panel stops propagating: CartItems treats any
 *     change event as a quantity edit and would POST garbage;
 *   - the controls carry form="CartMounting-detached" (a form that does not
 *     exist) so they are never posted with the checkout form and Enter in the
 *     length input does not submit anything;
 *   - ids derive from the line index so focus survives the list swap
 *     (cart-drawer.js restores focus by id first);
 *   - `aria-busy` instead of `disabled` while a write is in flight.
 *
 * Writes go one at a time. The drawer, when open, is refreshed from the
 * /cart/change response with the renamed key (a property write changes the
 * line key); when closed, a cartUpdate with source 'cart-mounting' makes
 * CartDrawerItems refetch. A failed reconcile write is remembered per line
 * and never retried in this page life (no 4xx loops).
 *
 * Measurement (2026-09-17): every customer write carries `_mount_by:
 * customer`, every reconcile answer `auto` (the planner sets it), and the
 * panel publishes `mounting_panel` events (assets/cart-mounting-analytics.js).
 *
 * Entry point (script tag in layout/theme.liquid, after pubsub.js like the
 * guards). Exports exist for tests/cart-mounting-element.test.mjs; side
 * effects live in boot().
 */
import { planMounting, makeWrite, findWrittenKey, propertiesEqual, unitSuspicion } from '#cart-mounting-state';
import { showDrawerNotice } from '#cart-line-sweep';
import { isValidSoleLength, KEYS, MOUNT_BY } from '#mounting-properties';
import { mountingEventPayload, publishMountingEvent } from '#cart-mounting-analytics';
import { installCheckoutGate, registerCheckoutWaiter, registerCheckoutBlocker } from '#checkout-gate';

export const SOURCE = 'cart-mounting';
export const NOTICE_ATTR = 'data-mounting-notice';
export const NOTICE_CLASS = 'cart-drawer__consultation-notice cart-drawer__mounting-notice';

/** Module state shared by every root instance (one drawer per page). */
export const writer = {
  inFlight: null,
  changedCart: false,
  // A customer action that arrives while a write is in flight is queued
  // (latest wins) and applied after the re-render, never dropped: dropping it
  // snapped the radio back to the previous choice (Gero, 16.09.2026).
  queued: null,
  // The drawer swap promise of the current write; run() waits for it so a
  // stale panel can never take a click after the cart moved on.
  rendering: null,
  // A write answered while the customer is in the length field: the
  // response is kept and rendered once the field is left, so the keyboard
  // survives (16.09.2026). Keys in the DOM are patched meanwhile.
  deferred: null,
  // Focus this element id after the next render (the length field after the
  // ski select saved the open state).
  focusAfterRender: null,
  attempted: new Set(),
  // Last reconcile write per line (`variantId:index` → `rule:choice`). The same
  // rule demanding the same choice on the same line right after its own write
  // means facts and write disagree: reconcile stops instead of rewriting the
  // line forever. A render that no longer asks for a write on the line clears
  // its entry.
  lastReconcile: new Map(),
  loopWarned: false,
  // Panels already reported as shown (`variantId:index`), once per page life.
  shown: new Set(),
  // Summary lines the customer opened with "Ändern" (`variantId:index`). A
  // line closes again once a customer write on it lands, or when the drawer
  // closes.
  editOpen: new Set(),
  reset() {
    this.inFlight = null;
    this.changedCart = false;
    this.queued = null;
    this.rendering = null;
    this.deferred = null;
    this.focusAfterRender = null;
    this.attempted.clear();
    this.lastReconcile.clear();
    this.loopWarned = false;
    this.shown.clear();
    this.editOpen.clear();
  },
};

/** One `mounting_panel` event; false without analytics consent. Never throws. */
export function track(action, unit = null, extra = {}) {
  try {
    return publishMountingEvent(mountingEventPayload(action, unit, extra));
  } catch {
    return false;
  }
}

/** The customer's answer: marks the write, also through the queue replay. */
function byCustomer(opts) {
  return { ...opts, by: MOUNT_BY.customer };
}

export function readFacts(root) {
  try {
    const el = root.querySelector('script[data-cart-mounting-facts]');
    if (!el) return null;
    const facts = JSON.parse(el.textContent);
    return facts && typeof facts === 'object' ? facts : null;
  } catch {
    return null;
  }
}

function strings() {
  return globalThis.window?.cartStrings || {};
}

function fill(template, title, ski, width) {
  return template
    ? String(template).replace('[title]', title || '').replace('[ski]', ski || '').replace('[width]', width == null ? '' : String(width))
    : '';
}

function findPanel(region, key) {
  for (const panel of region.querySelectorAll('[data-cart-mounting]')) {
    if (panel.dataset.lineKey === key) return panel;
  }
  return null;
}

/** Stable id of a line across the key change of a property write. */
function lineId(unit) {
  return `${unit.variantId}:${unit.index}`;
}

/** Is the panel of this unit shown: planned open, or opened through "Ändern". */
export function isPanelOpen(unit) {
  if (!unit?.panel) return false;
  return unit.expanded !== false || writer.editOpen.has(lineId(unit));
}

function bootKeyOf(unit, variantId) {
  return unit.options.boots.find((b) => b.variantId === variantId)?.key ?? null;
}

/** Fills one panel from its planned unit. Pure DOM, no requests. */
export function renderPanel(panel, unit) {
  // The panel carries data-line-key itself; the row is the nearest ancestor.
  const row = panel.parentElement?.closest('[data-line-key]') ?? null;
  const metaRows = row ? row.querySelectorAll('[data-mounting-meta]') : [];
  const editButton = row ? row.querySelector('[data-mounting-edit]') : null;
  // Summary (12.10.2026): a ski with its binding whose answer is complete shows
  // that answer as text with an "Ändern" link instead of the whole panel; the
  // link opens the panel for this line until the next customer write lands.
  const shown = isPanelOpen(unit);
  panel.hidden = !shown;
  metaRows.forEach((el) => { el.hidden = shown; });
  if (editButton) editButton.hidden = !unit.panel || shown;
  if (!shown) return;

  const index = panel.dataset.lineIndex || unit.index;
  const doc = panel.ownerDocument;
  const optionInputs = () => Array.from(panel.querySelectorAll('input[data-mounting-option]'));

  // Pending option: visible while the setting is on or the line is pending.
  const pendingLabel = panel.querySelector('[data-mounting-pending]');
  if (pendingLabel) {
    pendingLabel.hidden = !unit.options.pending;
    const desc = pendingLabel.querySelector('[data-mounting-pending-desc]');
    if (desc) desc.textContent = unit.drilled ? desc.dataset.copyDrilled || '' : desc.dataset.copyStandard || '';
  }

  // Boot options: one radio per boot line, cloned from the template.
  const bootHost = panel.querySelector('[data-mounting-boots]');
  const bootTemplate = panel.querySelector('template[data-mounting-boot-template]');
  if (bootHost && bootTemplate) {
    bootHost.textContent = '';
    unit.options.boots.forEach((boot, i) => {
      const fragment = bootTemplate.content.cloneNode(true);
      const input = fragment.querySelector('input');
      const label = fragment.querySelector('[data-boot-label]');
      input.id = `CartMounting-${index}-boot-${i + 1}`;
      input.dataset.bootKey = boot.key;
      const wrapper = fragment.querySelector('label') || fragment.firstElementChild;
      if (wrapper && wrapper.tagName === 'LABEL') wrapper.setAttribute('for', input.id);
      if (label) {
        label.textContent = unit.options.boots.length === 1
          ? panel.dataset.copyBootSingle || ''
          : fill(panel.dataset.copyBootNamed, boot.label);
      }
      bootHost.appendChild(fragment);
    });
  }

  // Ski pairing for a loose binding.
  const skiBlock = panel.querySelector('[data-mounting-ski]');
  const skiSelect = panel.querySelector('[data-mounting-ski-select]');
  const skiImplicit = panel.querySelector('[data-mounting-ski-implicit]');
  const skiHint = panel.querySelector('[data-mounting-ski-hint]');
  if (unit.role === 'binding' && unit.ski) {
    const offered = unit.ski.all?.length ? unit.ski.all : unit.ski.candidates;
    const many = offered.length > 1;
    if (skiSelect) {
      skiSelect.textContent = '';
      const placeholder = doc.createElement('option');
      placeholder.value = '';
      placeholder.textContent = skiSelect.dataset.placeholder || '';
      placeholder.disabled = true;
      placeholder.selected = !unit.ski.selected;
      skiSelect.appendChild(placeholder);
      offered.forEach((c) => {
        const option = doc.createElement('option');
        option.value = c.key;
        const held = c.heldBy?.length ? strings().mountingSkiHeld : '';
        option.textContent = held ? `${c.label} · ${held}` : c.label;
        option.selected = unit.ski.selected?.key === c.key;
        skiSelect.appendChild(option);
      });
    }
    if (skiBlock) skiBlock.dataset.hasContent = many ? 'true' : 'false';
    if (skiImplicit) {
      // "Montage auf Ski: ..." states a saved pairing, so it needs the saved
      // choice to be mounted, not just the radio (a ticked "bsl" without a
      // length has written nothing yet). The select stays available before
      // the write because the choice of ski is part of it.
      const savedMounted = unit.state?.choice === 'bsl' || unit.state?.choice === 'pending' || unit.state?.choice === 'boot';
      skiImplicit.dataset.hasContent = !many && unit.ski.selected && savedMounted ? 'true' : 'false';
      const labelEl = skiImplicit.querySelector('[data-mounting-ski-label]');
      if (labelEl) labelEl.textContent = unit.ski.selected?.label || '';
    }
    // "No ski left" only when the cart really has none for this binding;
    // when the brake fit ruled every ski out, the fit hint says why instead.
    if (skiHint) skiHint.hidden = !unit.options.mountingDisabled || unit.fit?.status === 'incompatible';
  } else {
    if (skiBlock) skiBlock.dataset.hasContent = 'false';
    if (skiImplicit) skiImplicit.dataset.hasContent = 'false';
    if (skiHint) skiHint.hidden = true;
  }

  // Brake fit (CFG-13 in the cart, 2026-09-16): the configurator's own copy.
  // 'incompatible' explains the disabled radios and always shows; 'unknown'
  // and 'waist-missing' only matter for a mounted choice (syncVisibility).
  const fitHint = panel.querySelector('[data-mounting-fit-hint]');
  if (fitHint) {
    const status = unit.fit?.status || '';
    const text = status === 'incompatible' ? fill(strings().mountingBrakeMismatch, '', '', unit.fit.maxBrakeMm)
      : status === 'unknown' ? (strings().mountingBrakeUnknown || '')
        : status === 'waist-missing' ? (strings().mountingWaistMissing || '') : '';
    fitHint.textContent = text;
    fitHint.dataset.fit = text ? status : '';
  }

  // Mounting disabled (no ski left for this binding): only "unmounted" stays.
  optionInputs().forEach((input) => {
    input.disabled = unit.options.mountingDisabled && input.value !== 'unmounted';
  });

  clearSkiRequired(panel);

  // Current selection.
  const { choice, mm, bootVariant } = unit.selection;
  optionInputs().forEach((input) => {
    if (choice === 'boot') input.checked = input.value === 'boot' && input.dataset.bootKey === bootKeyOf(unit, bootVariant);
    else input.checked = input.value === choice;
  });
  const mmInput = panel.querySelector('[data-mounting-mm]');
  if (mmInput) {
    mmInput.min = String(unit.bsl.min);
    mmInput.max = String(unit.bsl.max);
    mmInput.value = choice === 'bsl' && mm != null ? String(mm) : '';
    mmInput.setAttribute('aria-invalid', 'false');
  }
  // "Montieren, Länge offen": the choice is saved, the length is not. The
  // hint shows, and the checkout blocker below refuses to check out with it.
  const open = choice === 'bsl' && mm == null && unit.panel;
  panel.dataset.mountingOpen = open ? 'true' : 'false';
  const openHint = panel.querySelector('[data-mounting-open-hint]');
  if (openHint) openHint.hidden = !open;
  const error = panel.querySelector('[data-mounting-error]');
  if (error) error.hidden = true;
  renderSuspect(panel, choice === 'bsl' ? unit.suspect : null, mm);
  panel.setAttribute('aria-busy', 'false');
  syncVisibility(panel);
}

/**
 * The shoe-size hint (snippets/sole-length-help.liquid) under the length
 * field. It takes the place of the where-it-is-printed sentence, so the panel
 * keeps its height. `verdict` is unitSuspicion's answer, null hides the hint.
 */
export function renderSuspect(panel, verdict, mm) {
  const box = panel.querySelector('[data-bsl-suspect]');
  const where = panel.querySelector('[data-bsl-where]');
  if (where) where.hidden = !!verdict;
  if (!box) return;
  box.hidden = !verdict;
  if (!verdict) return;
  const text = box.querySelector('[data-bsl-suspect-text]');
  const copy = (verdict.reason === 'boot_size' ? box.dataset.copyBoot : box.dataset.copySize) || '';
  if (text) text.textContent = copy.split('[mm]').join(String(mm).trim()).split('[size]').join(verdict.size);
}

/** Shows the length field only under a checked bsl radio, the ski block only for a mounted choice. */
export function syncVisibility(panel) {
  const checked = panel.querySelector('input[data-mounting-option]:checked');
  const value = checked?.value ?? null;
  const bslRegion = panel.querySelector('[data-mounting-bsl]');
  if (bslRegion) bslRegion.hidden = value !== 'bsl';
  // The ski pairing only matters for a mounted choice; "unmounted" and
  // "nothing chosen yet" keep it out of the way.
  const showSki = value !== null && value !== 'unmounted';
  [panel.querySelector('[data-mounting-ski]'), panel.querySelector('[data-mounting-ski-implicit]')].forEach((el) => {
    if (el) el.hidden = !(showSki && el.dataset.hasContent === 'true');
  });
  // The ski question belongs to the ticked option and comes before its
  // length field: with several skis nothing can be saved until it is
  // answered, and at the bottom of the panel nobody found it (live
  // 17.09.2026). The ticked label itself never moves.
  const skiBlock = panel.querySelector('[data-mounting-ski]');
  const tickedLabel = checked?.closest('label');
  if (skiBlock && !skiBlock.hidden && tickedLabel && tickedLabel.nextElementSibling !== skiBlock) tickedLabel.after(skiBlock);
  if (!showSki) clearSkiRequired(panel);
  const fitHint = panel.querySelector('[data-mounting-fit-hint]');
  if (fitHint) {
    const status = fitHint.dataset.fit || '';
    fitHint.hidden = !(status === 'incompatible' || (showSki && status !== ''));
  }
}

/** True while a loose binding's mounted choice cannot be saved: several skis, none picked. */
export function skiOpen(panel) {
  const select = panel.querySelector('[data-mounting-ski-select]');
  const block = panel.querySelector('[data-mounting-ski]');
  const checked = panel.querySelector('input[data-mounting-option]:checked');
  return !!select && !!block && !block.hidden && !select.value && !!checked && checked.value !== 'unmounted';
}

/** "Wähle zuerst den Ski …" at the select, which takes the focus. */
export function showSkiRequired(panel, { focus = true } = {}) {
  const select = panel.querySelector('[data-mounting-ski-select]');
  const error = panel.querySelector('[data-mounting-ski-error]');
  if (error) error.hidden = false;
  select?.setAttribute('aria-invalid', 'true');
  if (focus) select?.focus();
}

export function clearSkiRequired(panel) {
  const error = panel.querySelector('[data-mounting-ski-error]');
  if (error) error.hidden = true;
  panel.querySelector('[data-mounting-ski-select]')?.setAttribute('aria-invalid', 'false');
}

/**
 * POST /cart/change with the drawer sections. Returns the cart JSON.
 * `fetchImpl` and `routes` are injectable for tests.
 */
export async function postWrite(write, { fetchImpl = globalThis.fetch, changeUrl, sectionsUrl } = {}) {
  const url = changeUrl || globalThis.window?.routes?.cart_change_url || '/cart/change';
  const response = await fetchImpl(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: write.key,
      quantity: write.quantity,
      properties: write.properties,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: sectionsUrl ?? globalThis.location?.pathname ?? '/',
    }),
  });
  if (!response || !response.ok) {
    const error = new Error(`cart/change ${response?.status ?? 'failed'}`);
    error.status = response?.status;
    throw error;
  }
  return response.json();
}

/**
 * Forms the pair in the DOM the moment the customer ticks a mounting option
 * (16.09.2026): the ski row moves right before the binding row and both get
 * the pair classes, the connector line is added. The binding row (with the
 * focused length field) never moves, so the keyboard survives. The server
 * render (snippets/cart-drawer.liquid) produces the same rows later.
 */
export function formPairInDom(region, panel, skiKey, doc = globalThis.document) {
  if (!region || !panel || !skiKey) return false;
  const bindingRow = panel.parentElement?.closest('[data-line-key]');
  const skiRow = Array.from(region.querySelectorAll('li[data-line-key]')).find((li) => li.dataset.lineKey === skiKey);
  if (!bindingRow || !skiRow || skiRow === bindingRow) return false;
  if (skiRow.nextElementSibling !== bindingRow && !skiRow.classList.contains('cart-drawer__item--pair-ski')) bindingRow.before(skiRow);
  skiRow.classList.add('cart-drawer__item--pair-ski');
  bindingRow.classList.add('cart-drawer__item--pair-binding');
  if (!bindingRow.querySelector('.cart-drawer__pair-connector')) {
    const connector = doc.createElement('p');
    connector.className = 'cart-drawer__pair-connector';
    connector.textContent = strings().mountingPairConnector || panel.dataset.copyPairConnector || '';
    bindingRow.prepend(connector);
  }
  return true;
}

/** True when the customer is typing in the written line's length field (its key already patched). */
function customerInLengthField(write, doc, newKey) {
  const active = doc.activeElement;
  if (!active || !active.matches?.('[data-mounting-mm]')) return false;
  const panel = active.closest('[data-cart-mounting]');
  return !!panel && (panel.dataset.lineKey === newKey || panel.dataset.lineKey === write.key);
}

/** Renders the response held back while the customer was in the field. */
export function applyDeferredRender(doc = globalThis.document) {
  const deferred = writer.deferred;
  if (!deferred || writer.inFlight) return false;
  writer.deferred = null;
  const drawer = doc.querySelector('cart-drawer');
  if (typeof drawer?.renderContents !== 'function') return false;
  drawer.renderContents(deferred.cart, { renamedKeys: {}, silent: true });
  return true;
}

/**
 * After a successful write. First the line's panel, its row and the unit
 * take the written key and properties, so any follow-up action (a queued
 * click, the blur timer) never posts against a stale key (a 400 that then
 * repainted the panel from the old state, 16.09.2026). Then the drawer is
 * rendered from the response, silently (never opening it), unless the
 * customer is typing in this line's length field: then the render is held
 * (applyDeferredRender) so the keyboard survives. Without a renderable
 * drawer the cart update is published and CartDrawerItems refetches.
 */
export function applyWrittenCart(cart, write, unit, doc = globalThis.document) {
  const newKey = findWrittenKey(cart, write, unit.variantId);
  const panel = Array.from(doc.querySelectorAll('[data-cart-mounting]')).find((p) => p.dataset.lineKey === write.key) || null;
  if (panel) {
    const row = panel.parentElement?.closest('[data-line-key]');
    panel.dataset.lineKey = newKey;
    if (row) row.dataset.lineKey = newKey;
  }
  unit.key = newKey;
  unit.props = write.properties;
  const drawer = doc.querySelector('cart-drawer');
  const canRender = typeof drawer?.renderContents === 'function' && cart?.sections;
  if (canRender && customerInLengthField(write, doc, newKey)) {
    const open = !write.properties['Sohlenlänge (mm)'];
    panel.dataset.mountingOpen = open ? 'true' : 'false';
    const hint = panel.querySelector('[data-mounting-open-hint]');
    if (hint) hint.hidden = !open;
    writer.deferred = { cart, oldKey: write.key, newKey };
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    }
    return newKey;
  }
  writer.deferred = null;
  if (canRender) {
    writer.rendering = drawer.renderContents(cart, { renamedKeys: { [write.key]: newKey }, silent: true }) ?? null;
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart });
    }
  } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
  }
  return newKey;
}

export class CartMountingRoot extends HTMLElement {
  connectedCallback() {
    // Siblings of this element (the rows with the panels) are inserted by the
    // same innerHTML assignment; a microtask puts us after that insertion.
    queueMicrotask(() => { if (this.isConnected) this.setup(); });
    this.consentListener = () => this.onConsentChange();
    globalThis.document?.addEventListener('visitorConsentCollected', this.consentListener);
    globalThis.document?.addEventListener(ANALYTICS_READY_EVENT, this.consentListener);
  }

  setup() {
    const facts = readFacts(this);
    if (!facts) {
      track('boot_failed');
      return;
    }
    const region = this.closest('#CartDrawer-CartItems') || this.parentElement;
    if (!region) return;
    const plan = planMounting(facts);
    this.plan = plan;
    this.units = new Map(plan.units.map((u) => [u.key, u]));
    for (const unit of plan.units) {
      const panel = findPanel(region, unit.key);
      if (!panel) continue;
      renderPanel(panel, unit);
      this.wire(panel, unit);
      this.wireEdit(panel, unit);
      if (isPanelOpen(unit)) this.watchShown(panel, unit);
    }
    this.flushQueued(region);
    this.reconcile(plan, region);
  }

  /**
   * `shown` means seen: the drawer markup (and with it this element) loads
   * with every page, long before the customer opens the cart and before
   * Shopify's consent API is ready, so the impression is reported when at
   * least half of the panel is on screen (like the cart recommendations).
   * Without consent the panel stays pending and is retried when the visitor
   * decides. Once per line and page life (`writer.shown`).
   */
  watchShown(panel, unit) {
    const key = `${unit.variantId}:${unit.index}`;
    if (writer.shown.has(key)) return;
    const report = () => {
      if (writer.shown.has(key)) return true;
      if (!track('shown', unit)) return false;
      writer.shown.add(key);
      return true;
    };
    this.pendingShown = this.pendingShown || new Map();
    if (typeof IntersectionObserver === 'undefined') {
      // No layout (tests, very old browsers): an open drawer counts as seen.
      if (panel.closest('cart-drawer')?.classList.contains('active') && !report()) this.pendingShown.set(key, report);
      return;
    }
    if (!this.shownObserver) {
      this.shownTargets = new Map();
      this.shownObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const target = this.shownTargets.get(entry.target);
          if (!target) continue;
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
            this.pendingShown.delete(target.key);
            continue;
          }
          if (target.report()) this.shownObserver.unobserve(entry.target);
          else this.pendingShown.set(target.key, target.report);
        }
      }, { threshold: 0.5 });
    }
    this.shownTargets.set(panel, { key, report });
    this.shownObserver.observe(panel);
  }

  /** Re-applies summary/open to every panel of the current plan (drawer closed). */
  refreshModes() {
    const region = this.closest('#CartDrawer-CartItems') || this.parentElement;
    if (!region || !this.plan) return;
    for (const unit of this.plan.units) {
      const panel = findPanel(region, unit.key);
      if (panel) renderPanel(panel, unit);
    }
  }

  /** The visitor decided on consent: report the panels that are on screen right now. */
  onConsentChange() {
    for (const [key, report] of this.pendingShown || []) {
      if (report()) this.pendingShown.delete(key);
    }
  }

  disconnectedCallback() {
    this.shownObserver?.disconnect();
    if (this.consentListener) {
      globalThis.document?.removeEventListener('visitorConsentCollected', this.consentListener);
      globalThis.document?.removeEventListener(ANALYTICS_READY_EVENT, this.consentListener);
    }
  }

  /**
   * Applies the customer action queued during the last write to the line's
   * fresh panel (same cart position and variant, new key). Runs after every
   * render and after every settled write; a no-op while a write is in flight.
   */
  flushQueued(region = this.closest('#CartDrawer-CartItems') || this.parentElement) {
    const q = writer.queued;
    if (!q || writer.inFlight || !this.plan || !region) return undefined;
    writer.queued = null;
    const unit = this.plan.units.find((u) => u.index === q.index && u.variantId === q.variantId);
    const panel = unit ? findPanel(region, unit.key) : null;
    if (!unit || !panel || !unit.panel) return undefined;
    // The intent may be impossible now (the last write claimed the only ski
    // for another binding): then the panel keeps showing the saved state.
    if (!makeWrite(unit, q.choice, q.opts)) return undefined;
    const radio = panel.querySelector(`input[data-mounting-option][value="${q.choice}"]`);
    if (radio) {
      radio.checked = true;
      syncVisibility(panel);
    }
    if (q.choice === 'bsl' && q.opts?.mm != null && String(q.opts.mm).trim() !== '') {
      const mm = panel.querySelector('[data-mounting-mm]');
      if (mm) mm.value = String(q.opts.mm);
      return this.onLength(panel, unit);
    }
    return this.commit(panel, unit, q.choice, q.opts);
  }

  wire(panel, unit) {
    if (panel.dataset.wired === 'true') return;
    panel.dataset.wired = 'true';
    panel.addEventListener('change', (event) => {
      // CartItems treats every change as a quantity edit: never let it see ours.
      event.stopPropagation();
      // A customer action starts a new story: an older reconcile notice goes
      // (a queued follow-up write keeps the notice its swap just showed).
      showDrawerNotice('', { noticeAttr: NOTICE_ATTR, noticeClass: NOTICE_CLASS });
      const target = event.target;
      if (target.matches('input[data-mounting-option]')) return this.onChoice(panel, unit, target);
      // The blur onApply causes: its click is the one write.
      if (target.matches('[data-mounting-mm]')) return panel._mountingApplying ? undefined : this.onLength(panel, unit);
      if (target.matches('[data-mounting-ski-select]')) return this.onSki(panel, unit);
      return undefined;
    });
    panel.addEventListener('input', (event) => {
      event.stopPropagation();
      // Typing answers the length message: it goes until the next check
      // (preview PR #455: it stayed red under a valid 305).
      if (event.target.matches('[data-mounting-mm]') && event.target.getAttribute('aria-invalid') === 'true') {
        event.target.setAttribute('aria-invalid', 'false');
        const error = panel.querySelector('[data-mounting-error]');
        if (error) error.hidden = true;
      }
    });
    panel.addEventListener('focusout', (event) => {
      if (event.target.matches('[data-mounting-mm]')) this.onLengthBlur(panel, unit);
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-mounting-mm]')) {
        event.preventDefault();
        this.onLength(panel, unit);
      }
    });
    // "Übernehmen" (22.10.2026). The press keeps the focus in the length
    // field, so the field's change does not run ahead of the click; onApply
    // leaves the field itself.
    panel.addEventListener('mousedown', (event) => {
      if (event.target.closest?.('[data-mounting-apply]')) event.preventDefault();
    });
    panel.addEventListener('click', (event) => {
      if (!event.target.closest?.('[data-mounting-apply]')) return;
      event.preventDefault();
      showDrawerNotice('', { noticeAttr: NOTICE_ATTR, noticeClass: NOTICE_CLASS });
      this.onApply(panel, unit);
    });
  }

  /**
   * "Ändern" on a summary line (12.10.2026): opens the panel for that line,
   * moves the keyboard to the saved answer and reports `edit_open`. The link
   * lives in the line's meta (snippets/cart-drawer-item.liquid), outside the
   * panel, so it is wired here and not in wire().
   */
  wireEdit(panel, unit) {
    const row = panel.parentElement?.closest('[data-line-key]') ?? null;
    const button = row?.querySelector('[data-mounting-edit]');
    if (!button || button.dataset.wired === 'true') return;
    button.dataset.wired = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      writer.editOpen.add(lineId(unit));
      track('edit_open', unit);
      renderPanel(panel, unit);
      this.watchShown(panel, unit);
      const target = panel.querySelector('input[data-mounting-option]:checked') || panel.querySelector('input[data-mounting-option]');
      target?.focus?.({ preventScroll: true });
      try { panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch { /* jsdom */ }
    });
  }

  currentSkiKey(panel, unit) {
    const select = panel.querySelector('[data-mounting-ski-select]');
    if (select && !select.hidden && select.value) return select.value;
    return unit.ski?.selected?.key ?? null;
  }

  onChoice(panel, unit, input) {
    syncVisibility(panel);
    const value = input.value;
    track('choice', unit, { choice: value, by: MOUNT_BY.customer });
    // Several skis, none picked: the ski comes first. The select sits right
    // under the ticked option now and takes the focus inside the tap; its
    // change (onSki) saves the ticked choice. Without this a tick on "reiche
    // ich nach" saved nothing and said nothing.
    if (skiOpen(panel)) {
      panel.querySelector('[data-mounting-ski-select]')?.focus();
      return undefined;
    }
    if (value === 'bsl') {
      const mm = panel.querySelector('[data-mounting-mm]');
      if (mm && mm.value.trim() !== '') return this.onLength(panel, unit);
      // The tick forms the pair right away and saves the open state; the
      // customer is put into the length field inside the tap (keyboard). The
      // response render is held back while the field has the focus
      // (applyWrittenCart), so the field is never replaced under the finger.
      // With several skis the select comes first (onSki writes and focuses).
      const skiKey = this.currentSkiKey(panel, unit);
      mm?.focus();
      if (skiKey && makeWrite(unit, 'bsl', { mm: null, skiKey })) {
        formPairInDom(panel.closest('#CartDrawer-CartItems') || panel.parentElement?.parentElement, panel, skiKey);
      }
      return this.commit(panel, unit, 'bsl', { mm: null, skiKey });
    }
    if (value === 'boot') return this.commit(panel, unit, 'boot', { bootKey: input.dataset.bootKey, skiKey: this.currentSkiKey(panel, unit) });
    return this.commit(panel, unit, value, { skiKey: this.currentSkiKey(panel, unit) });
  }

  /**
   * The field was left. Empty and still under a ticked bsl radio a moment
   * later: save the open state. The short wait lets a click on another radio
   * (blur first, change second) win without a wasted write; a value is
   * handled by the change event (onLength) instead.
   */
  onLengthBlur(panel, unit) {
    clearTimeout(panel._mountingBlurTimer);
    panel._mountingBlurTimer = setTimeout(() => {
      if (!panel.isConnected) return;
      const mm = panel.querySelector('[data-mounting-mm]');
      const bsl = panel.querySelector('input[data-mounting-option][value="bsl"]');
      if (!mm || mm === panel.ownerDocument.activeElement) return;
      // Empty under a ticked bsl radio: usually already saved on the tick
      // (then a no-op); otherwise save now, and that response renders.
      if (bsl?.checked && mm.value.trim() === '' && this.commit(panel, unit, 'bsl', { mm: null, skiKey: this.currentSkiKey(panel, unit) })) return;
      // Any value: a length saved with Enter held its render while the field
      // had the focus; without this the line never folded back to its
      // summary after "Ändern" (preview probe 12.10.2026).
      applyDeferredRender(panel.ownerDocument);
    }, 200);
  }

  onLength(panel, unit) {
    const mm = panel.querySelector('[data-mounting-mm]');
    const error = panel.querySelector('[data-mounting-error]');
    const bslRadio = panel.querySelector('input[data-mounting-option][value="bsl"]');
    if (!mm) return undefined;
    if (bslRadio && !bslRadio.checked) {
      bslRadio.checked = true;
      syncVisibility(panel);
    }
    const skiKey = this.currentSkiKey(panel, unit);
    if (unit.role === 'binding' && !skiKey && skiOpen(panel)) {
      // The length may be fine: what is missing is the ski. The typed value
      // stays in the field and is written once the ski is picked (onSki).
      if (error) error.hidden = true;
      mm.setAttribute('aria-invalid', 'false');
      showSkiRequired(panel);
      track('length_invalid', unit, { choice: 'bsl', error_reason: 'no_ski' });
      return undefined;
    }
    const write = makeWrite(unit, 'bsl', byCustomer({ mm: mm.value, skiKey }));
    if (!write) {
      renderSuspect(panel, null, mm.value);
      if (error) error.hidden = false;
      mm.setAttribute('aria-invalid', 'true');
      track('length_invalid', unit, { choice: 'bsl', error_reason: unit.role === 'binding' && !skiKey ? 'no_ski' : 'range' });
      return undefined;
    }
    if (error) error.hidden = true;
    mm.setAttribute('aria-invalid', 'false');
    // Accepted all the same: the hint asks the customer to look again, the
    // length is written as typed (orders #1594, #1101).
    const verdict = unitSuspicion(unit, mm.value, skiKey);
    renderSuspect(panel, verdict, mm.value);
    if (writer.inFlight) {
      writer.queued = { index: unit.index, variantId: unit.variantId, choice: 'bsl', opts: { mm: mm.value, skiKey } };
      return writer.inFlight;
    }
    if (propertiesEqual(write.properties, unit.props)) return undefined;
    track('length_valid', unit, { choice: 'bsl', by: MOUNT_BY.customer });
    if (verdict) track('length_suspect', unit, { choice: 'bsl', error_reason: verdict.reason });
    return this.run(panel, unit, write, { customer: true });
  }

  /**
   * "Übernehmen" next to the length field (22.10.2026): saves the typed
   * length like Enter, but leaves the field first. The keyboard closes and
   * the response renders right away (applyWrittenCart holds it only while
   * the field has the focus), so the line folds back like after a tick. The
   * change of that blur is skipped: this click is the one write. An empty
   * field gets the range message instead of saving the open state again; the
   * length that is already saved (opened with "Ändern", or saved with Enter)
   * just folds.
   */
  onApply(panel, unit) {
    const mm = panel.querySelector('[data-mounting-mm]');
    if (!mm) return undefined;
    const doc = panel.ownerDocument;
    if (doc.activeElement === mm) {
      panel._mountingApplying = true;
      try { mm.blur(); } finally { panel._mountingApplying = false; }
    }
    if (mm.value.trim() === '' && !skiOpen(panel)) {
      const error = panel.querySelector('[data-mounting-error]');
      if (error) error.hidden = false;
      mm.setAttribute('aria-invalid', 'true');
      track('length_invalid', unit, { choice: 'bsl', error_reason: 'empty' });
      mm.focus();
      return undefined;
    }
    const job = this.onLength(panel, unit);
    if (job || mm.getAttribute('aria-invalid') === 'true' || skiOpen(panel)) return job;
    // Nothing to write, the field holds the saved length.
    if (applyDeferredRender(doc)) return undefined;
    if (writer.editOpen.delete(lineId(unit))) {
      renderPanel(panel, unit);
      const edit = doc.getElementById(`CartMounting-${unit.index}-edit`);
      if (edit && !edit.hidden) edit.focus();
    }
    return undefined;
  }

  /**
   * The chosen ski is held by another binding: that binding moves to this
   * line's ski (or to the one free ski) in a first write, this line's own
   * choice runs as the queued follow-up. Two explicit writes, because the
   * planner alone would let the earlier cart line win (16.09.2026).
   */
  swapForHeldSki(unit, skiKey) {
    if (!skiKey || !unit.ski || unit.ski.candidates.some((c) => c.key === skiKey)) return null;
    const target = unit.ski.all?.find((s) => s.key === skiKey);
    const holder = target?.heldBy?.[0] ? this.units.get(target.heldBy[0].key) : null;
    if (!holder || !holder.ski || !holder.state) return null;
    const mine = unit.ski.selected && !unit.ski.implicit ? unit.ski.selected.key : null;
    const dest = mine ? unit.ski.all.find((s) => s.key === mine) : holder.ski.candidates.find((c) => c.key !== skiKey);
    if (!dest) return null;
    const write = makeWrite(holder, holder.state.choice, { mm: holder.state.mm, bootKey: null, skiKey: dest.key, allowHeld: true });
    return write ? { holder, write, dest } : null;
  }

  onSki(panel, unit) {
    clearSkiRequired(panel);
    const checked = panel.querySelector('input[data-mounting-option]:checked');
    const value = checked?.value ?? null;
    if (!value || value === 'unmounted') return undefined;
    const skiKey = this.currentSkiKey(panel, unit);
    const swap = this.swapForHeldSki(unit, skiKey);
    track('ski_selected', unit, { choice: value, swap: !!swap });
    if (swap) {
      if (writer.inFlight) return writer.inFlight;
      const region = panel.closest('#CartDrawer-CartItems') || this.parentElement;
      const mm = panel.querySelector('[data-mounting-mm]');
      const opts = value === 'bsl'
        ? { mm: mm && mm.value.trim() !== '' ? mm.value : null, skiKey }
        : { bootKey: checked.dataset.bootKey, skiKey };
      if (value === 'bsl' && !opts.mm && mm?.id) writer.focusAfterRender = mm.id;
      const job = this.run(findPanel(region, swap.holder.key), swap.holder, swap.write, { customer: true, notice: { template: 'mountingSkiReassigned', ski: swap.dest.label } });
      writer.queued = { index: unit.index, variantId: unit.variantId, choice: value, opts };
      return job;
    }
    if (value === 'bsl') {
      const mm = panel.querySelector('[data-mounting-mm]');
      if (mm && mm.value.trim() !== '') return this.onLength(panel, unit);
      // The ski is chosen: save the open state, render, then put the
      // customer into the length field.
      if (mm?.id) writer.focusAfterRender = mm.id;
      return this.commit(panel, unit, 'bsl', { mm: null, skiKey: this.currentSkiKey(panel, unit) });
    }
    return this.commit(panel, unit, value, { bootKey: checked.dataset.bootKey, skiKey: this.currentSkiKey(panel, unit) });
  }

  commit(panel, unit, choice, opts) {
    const write = makeWrite(unit, choice, byCustomer(opts));
    if (!write) return undefined;
    if (writer.inFlight) {
      writer.queued = { index: unit.index, variantId: unit.variantId, choice, opts };
      return writer.inFlight;
    }
    // The saved choice again (a re-click, or a queued intent the last write
    // already fulfilled): nothing to write.
    if (propertiesEqual(write.properties, unit.props)) return undefined;
    if (choice === 'bsl' && (opts?.mm == null || String(opts.mm).trim() === '')) track('open_saved', unit, { choice: 'bsl', by: MOUNT_BY.customer });
    return this.run(panel, unit, write, { customer: true });
  }

  /** One write at a time; a second request while one is in flight is dropped (the panel is aria-busy). */
  run(panel, unit, write, { customer = false, notice = null, attemptKey = null } = {}) {
    if (writer.inFlight) return writer.inFlight;
    writer.changedCart = false;
    writer.deferred = null; // this write's response supersedes a held one
    panel?.setAttribute('aria-busy', 'true');
    const job = (async () => {
      try {
        const cart = await postWrite(write);
        // The customer answered: a line opened through "Ändern" folds back to
        // its summary on the render this write triggers (unless the planner
        // keeps it open, e.g. for a suspect length).
        if (customer) writer.editOpen.delete(lineId(unit));
        if (notice) {
          showDrawerNotice(fill(strings()[notice.template], unit.label, notice.ski, notice.width), { noticeAttr: NOTICE_ATTR, noticeClass: NOTICE_CLASS });
          writer.changedCart = true;
        }
        applyWrittenCart(cart, write, unit);
        // Stay in flight until the drawer swap has landed: the old panel is
        // gone before a next click can reach it.
        await writer.rendering;
        if (writer.focusAfterRender) {
          const id = writer.focusAfterRender;
          writer.focusAfterRender = null;
          globalThis.document?.getElementById?.(id)?.focus?.();
        } else if (customer) {
          // Folded back: the control the customer used is hidden now, so the
          // keyboard continues on the line's "Ändern" link.
          const edit = globalThis.document?.getElementById?.(`CartMounting-${unit.index}-edit`);
          if (edit && !edit.hidden) edit.focus?.();
        }
      } catch (error) {
        if (attemptKey) writer.attempted.add(attemptKey);
        track('write_failed', unit, { choice: write.choice, by: customer ? MOUNT_BY.customer : MOUNT_BY.auto, error_reason: error?.status ?? 'network' });
        if (customer) {
          showDrawerNotice(strings().mountingError || '', { noticeAttr: NOTICE_ATTR, noticeClass: NOTICE_CLASS });
          if (panel?.isConnected) renderPanel(panel, unit);
        }
      } finally {
        writer.inFlight = null;
        writer.rendering = null;
        if (panel?.isConnected) panel.setAttribute('aria-busy', 'false');
        // A click that arrived meanwhile: apply it to the live root's panel.
        // Otherwise the fresh root's reconcile pass, which its setup skipped
        // because this write was still in flight during the swap (16.09.2026:
        // a binding that lost its ski to the other panel's switch kept its
        // stale pairing and never regrouped). A held render has a stale root:
        // its pass runs when the render lands.
        const live = globalThis.document?.querySelector?.('cart-mounting-root');
        if (writer.queued && live && typeof live.flushQueued === 'function') live.flushQueued();
        else if (!writer.deferred && live?.plan && typeof live.reconcile === 'function') {
          live.reconcile(live.plan, live.closest('#CartDrawer-CartItems') || live.parentElement);
        }
      }
    })();
    writer.inFlight = job;
    return job;
  }

  /**
   * At most one reconcile write per render, never while a write is in flight, never twice
   * for the same line + payload, and never the same rule + choice again on a line whose
   * last reconcile write has not settled: then facts and write disagree (15.09.2026, Liquid
   * rendered properties as [key, value] pairs and R0 re-nested them on every render), so
   * reconcile stops and warns once. A render that no longer asks for a write on a line
   * clears that line's entry, so a later legitimate repeat (binding removed and added
   * again) still gets its write.
   */
  reconcile(plan, region) {
    if (writer.inFlight) return null;
    const lineKeyOf = (unit) => `${unit.variantId}:${unit.index}`;
    const demanded = new Set(plan.writes.map((w) => this.units.get(w.key)).filter(Boolean).map(lineKeyOf));
    for (const key of [...writer.lastReconcile.keys()]) {
      if (!demanded.has(key)) writer.lastReconcile.delete(key);
    }
    for (const write of plan.writes) {
      const unit = this.units.get(write.key);
      if (!unit) continue;
      const attemptKey = `${unit.index}:${JSON.stringify(write.properties)}`;
      // Only FAILED reconcile writes are remembered (no 4xx loop); a write
      // that landed may legitimately be needed again later in the page life
      // (16.09.2026: the second ski switch never moved the other binding).
      if (writer.attempted.has(attemptKey)) continue;
      const rule = unit.reconcile;
      const ruleKey = `${rule?.rule ?? ''}:${write.choice}`;
      if (writer.lastReconcile.get(lineKeyOf(unit)) === ruleKey) {
        if (!writer.loopWarned) {
          writer.loopWarned = true;
          globalThis.console?.warn?.('[cart-mounting] reconcile stopped: the same write would repeat on one line', { line: lineKeyOf(unit), rule: ruleKey });
        }
        continue;
      }
      writer.lastReconcile.set(lineKeyOf(unit), ruleKey);
      let notice = null;
      if (rule?.notice === 'boot_removed') notice = { template: rule.next === 'pending' ? 'mountingBootRemovedPending' : 'mountingBootRemovedUnmounted' };
      if (rule?.notice === 'ski_removed') notice = { template: 'mountingSkiRemoved' };
      if (rule?.notice === 'ski_reassigned') notice = { template: 'mountingSkiReassigned', ski: rule.ski };
      if (rule?.notice === 'ski_incompatible') notice = { template: 'mountingSkiIncompatible', ski: rule.ski, width: rule.maxBrakeMm };
      track('reconcile', unit, { choice: write.choice, rule: rule?.rule, by: write.properties[KEYS.mountBy] });
      return this.run(findPanel(region, unit.key), unit, write, { customer: false, notice, attemptKey });
    }
    return null;
  }
}

function boot() {
  if (!customElements.get('cart-mounting-root')) customElements.define('cart-mounting-root', CartMountingRoot);
  installCheckoutGate();
  registerCheckoutWaiter({
    busy: () => !!writer.inFlight,
    inFlight: () => writer.inFlight,
    changedCart: () => writer.changedCart,
  });
  registerCheckoutBlocker(() => openLengthBlocker(document));
  watchDrawerClose(document);
}

/**
 * Lines opened through "Ändern" fold back when the drawer closes, so the next
 * visit starts from the summaries again. No event marks the close
 * (assets/cart-drawer.js only drops the `active` class), hence the observer.
 */
export function watchDrawerClose(doc = globalThis.document) {
  const drawer = doc?.querySelector?.('cart-drawer');
  const Observer = doc?.defaultView?.MutationObserver ?? globalThis.MutationObserver;
  if (!drawer || typeof Observer !== 'function' || drawer.dataset.mountingCloseWatch === 'true') return null;
  drawer.dataset.mountingCloseWatch = 'true';
  const observer = new Observer(() => {
    if (drawer.classList.contains('active') || writer.editOpen.size === 0) return;
    writer.editOpen.clear();
    doc.querySelector('cart-mounting-root')?.refreshModes?.();
  });
  observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  return observer;
}

/**
 * Checkout blocker: a line saved as "montieren, Länge offen" must not reach
 * the checkout. Returns null when nothing is open; otherwise a block() that
 * shows the required error on that panel, opens the drawer (the /cart page
 * carries the same drawer) and focuses the length field.
 */
export function openLengthBlocker(doc = globalThis.document) {
  const root = doc.querySelector('cart-mounting-root');
  for (const panel of doc.querySelectorAll('[data-cart-mounting]')) {
    if (panel.hidden) continue;
    if (skiOpen(panel)) {
      // A mounted option is ticked, the ski is not picked: nothing of it is
      // saved, the line still says what it said before. Not to the checkout.
      const ticked = panel.querySelector('input[data-mounting-option]:checked')?.value;
      const openUnit = root?.units ? [...root.units.values()].find((u) => u.key === panel.dataset.lineKey) ?? null : null;
      return {
        reason: 'mounting-ski-open',
        block() {
          track('checkout_blocked', openUnit, { choice: ticked, error_reason: 'no_ski' });
          const drawer = doc.querySelector('cart-drawer');
          if (drawer && !drawer.classList.contains('active') && typeof drawer.open === 'function') drawer.open();
          try { panel.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* jsdom */ }
          showSkiRequired(panel, { focus: false });
          panel.querySelector('[data-mounting-ski-select]')?.focus({ preventScroll: true });
        },
      };
    }
    const bsl = panel.querySelector('input[data-mounting-option][value="bsl"]');
    const mm = panel.querySelector('[data-mounting-mm]');
    const value = (mm?.value ?? '').trim();
    const savedOpen = panel.dataset.mountingOpen === 'true';
    // Saved open state, or a ticked bsl radio whose field is still empty (the
    // customer is in the field, nothing saved yet): both must not check out.
    if (!savedOpen && !(bsl?.checked && value === '')) continue;
    // By the unit's current key: a landed write renames the line and patches
    // unit.key and the panel, the map still holds the old key.
    const unit = root?.units ? [...root.units.values()].find((u) => u.key === panel.dataset.lineKey) ?? null : null;
    if (bsl?.checked && value !== '' && isValidSoleLength(value, Number(mm.min) || undefined, Number(mm.max) || undefined)) {
      // A valid number sits in the field, it only has not been written yet:
      // the write runs on change/blur and this click came first (Gero
      // 16.09.2026, "Sicher zur Kasse" straight from the field showed the
      // range error). Save it now; the checkout waiter holds the submit
      // until the write lands and the re-submit passes here.
      if (!writer.inFlight && unit && typeof root.onLength === 'function') {
        track('checkout_rescued', unit, { choice: 'bsl' });
        root.onLength(panel, unit);
      }
      if (writer.inFlight) continue;
    }
    return {
      reason: 'mounting-length-open',
      block() {
        track('checkout_blocked', unit, { choice: 'bsl', error_reason: savedOpen ? 'saved_open' : 'ticked_empty' });
        const error = panel.querySelector('[data-mounting-error]');
        const bslRegion = panel.querySelector('[data-mounting-bsl]');
        if (bslRegion) bslRegion.hidden = false;
        if (error) error.hidden = false;
        mm?.setAttribute('aria-invalid', 'true');
        const drawer = doc.querySelector('cart-drawer');
        if (drawer && !drawer.classList.contains('active') && typeof drawer.open === 'function') drawer.open();
        try { panel.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* jsdom */ }
        mm?.focus({ preventScroll: true });
      },
    };
  }
  return null;
}

if (typeof document !== 'undefined' && typeof customElements !== 'undefined') boot();
